import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  TextField,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Person as PersonIcon,
  LocalShipping as MachineIcon,
  Description as ReportIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { machineReportService } from '../../services/machineReportService';
import type { Customer, Machine } from '../../services/machineReportService';

const CustomerMachineReport: React.FC = () => {
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [selectedMachineId, setSelectedMachineId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);

  // Query for customers
  const { data: customersData, isLoading: loadingCustomers } = useQuery({
    queryKey: ['customers-with-contracts'],
    queryFn: () => machineReportService.getCustomersWithContracts(),
  });

  // Query for machines (only when customer is selected)
  const { data: machinesData, isLoading: loadingMachines } = useQuery({
    queryKey: ['customer-machines', selectedCustomerId],
    queryFn: () => machineReportService.getCustomerMachines(selectedCustomerId),
    enabled: !!selectedCustomerId,
  });

  // Query for machine refill history (only when machine is selected)
  const { data: refillHistoryData, isLoading: loadingHistory } = useQuery({
    queryKey: ['machine-refill-history', selectedMachineId, startDate, endDate],
    queryFn: () =>
      machineReportService.getMachineRefillHistory(selectedMachineId, {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }),
    enabled: !!selectedMachineId,
  });

  const customers = customersData?.data || [];
  const machines = machinesData?.data || [];
  const refillHistory = refillHistoryData?.data;

  const selectedCustomer = customers.find((c: Customer) => c.id === selectedCustomerId);

  const handleCustomerChange = (customerId: string) => {
    setSelectedCustomerId(customerId);
    setSelectedMachineId(''); // Reset machine selection
  };

  const handleDownloadPDF = async () => {
    if (!selectedMachineId) return;

    try {
      setIsDownloading(true);
      const blob = await machineReportService.downloadMachineReportPDF(selectedMachineId, {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `machine-report-${refillHistory?.machine.serialNumber || selectedMachineId}-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF report');
    } finally {
      setIsDownloading(false);
    }
  };

  const getStatusColor = (status: string): 'success' | 'default' | 'warning' | 'error' | 'info' | 'primary' | 'secondary' => {
    switch (status) {
      case 'RENTED':
        return 'success';
      case 'RETURNED':
        return 'default';
      case 'MAINTENANCE':
        return 'warning';
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReportIcon fontSize="large" />
        Customer Machine Report
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Select a customer and machine to view detailed refill history and export reports
      </Typography>

      <Grid container spacing={3}>
        {/* Selection Panel */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon />
                Selection
              </Typography>

              {/* Customer Selection */}
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Select Customer</InputLabel>
                <Select
                  value={selectedCustomerId}
                  onChange={(e) => handleCustomerChange(e.target.value as string)}
                  label="Select Customer"
                  disabled={loadingCustomers}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {customers.map((customer: Customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name} ({customer.rentalContracts.length} contracts)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Machine Selection */}
              {selectedCustomerId && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Select Machine</InputLabel>
                  <Select
                    value={selectedMachineId}
                    onChange={(e) => setSelectedMachineId(e.target.value as string)}
                    label="Select Machine"
                    disabled={loadingMachines || machines.length === 0}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {machines.map((machine: Machine) => (
                      <MenuItem key={machine.id} value={machine.id}>
                        {machine.serialNumber} - {machine.productName} ({machine.totalRefills} refills)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Date Range Filter */}
              {selectedMachineId && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DateRangeIcon fontSize="small" />
                    Date Range Filter (Optional)
                  </Typography>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start Date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  {(startDate || endDate) && (
                    <Button
                      size="small"
                      onClick={() => {
                        setStartDate('');
                        setEndDate('');
                      }}
                      sx={{ mt: 1 }}
                    >
                      Clear Dates
                    </Button>
                  )}
                </>
              )}

              {/* Export Button */}
              {selectedMachineId && refillHistory && (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={isDownloading ? <CircularProgress size={20} /> : <DownloadIcon />}
                  onClick={handleDownloadPDF}
                  disabled={isDownloading}
                  sx={{ mt: 3 }}
                >
                  {isDownloading ? 'Generating PDF...' : 'Export PDF Report'}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Customer Info Card */}
          {selectedCustomer && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Customer Details
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {selectedCustomer.name}
                </Typography>
                {selectedCustomer.phone && (
                  <Typography variant="body2">
                    <strong>Phone:</strong> {selectedCustomer.phone}
                  </Typography>
                )}
                {selectedCustomer.email && (
                  <Typography variant="body2">
                    <strong>Email:</strong> {selectedCustomer.email}
                  </Typography>
                )}
                {selectedCustomer.address && (
                  <Typography variant="body2">
                    <strong>Address:</strong> {selectedCustomer.address}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Report Display Panel */}
        <Grid item xs={12} md={8}>
          {!selectedCustomerId && (
            <Alert severity="info">Please select a customer to view their machines</Alert>
          )}

          {selectedCustomerId && !selectedMachineId && (
            <>
              {loadingMachines ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : machines.length === 0 ? (
                <Alert severity="warning">No machines found for this customer</Alert>
              ) : (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MachineIcon />
                      Available Machines ({machines.length})
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Serial Number</TableCell>
                            <TableCell>Product</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Contract</TableCell>
                            <TableCell align="center">Refills</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {machines.map((machine: Machine) => (
                            <TableRow
                              key={machine.id}
                              hover
                              onClick={() => setSelectedMachineId(machine.id)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>{machine.serialNumber}</TableCell>
                              <TableCell>{machine.productName}</TableCell>
                              <TableCell>
                                <Chip
                                  label={machine.status}
                                  color={getStatusColor(machine.status)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{machine.contractNumber}</TableCell>
                              <TableCell align="center">{machine.totalRefills}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {selectedMachineId && loadingHistory && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {selectedMachineId && !loadingHistory && refillHistory && (
            <>
              {/* Machine Information Card */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Machine Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Serial Number
                      </Typography>
                      <Typography variant="body1">{refillHistory.machine.serialNumber}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Product
                      </Typography>
                      <Typography variant="body1">{refillHistory.machine.productName}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                      <Chip
                        label={refillHistory.machine.status}
                        color={getStatusColor(refillHistory.machine.status)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Contract
                      </Typography>
                      <Typography variant="body1">{refillHistory.contract.contractNumber}</Typography>
                    </Grid>
                    {refillHistory.machine.rentStartDate && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Rent Start Date
                        </Typography>
                        <Typography variant="body1">
                          {new Date(refillHistory.machine.rentStartDate).toLocaleDateString()}
                        </Typography>
                      </Grid>
                    )}
                    {refillHistory.refillSchedule && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Next Refill
                        </Typography>
                        <Typography variant="body1">
                          {new Date(refillHistory.refillSchedule.nextRefillDate).toLocaleDateString()}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* Summary Statistics Card */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Summary Statistics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={4}>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.light', textAlign: 'center' }}>
                        <Typography variant="h4" color="primary.contrastText">
                          {refillHistory.summary.totalRefills}
                        </Typography>
                        <Typography variant="body2" color="primary.contrastText">
                          Total Refills
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.light', textAlign: 'center' }}>
                        <Typography variant="h4" color="success.contrastText">
                          {refillHistory.summary.totalOilUsedLiters}L
                        </Typography>
                        <Typography variant="body2" color="success.contrastText">
                          Oil Used
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: 'info.light', textAlign: 'center' }}>
                        <Typography variant="h4" color="info.contrastText">
                          {refillHistory.summary.oilScentsUsed.length}
                        </Typography>
                        <Typography variant="body2" color="info.contrastText">
                          Oil Types
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  {refillHistory.summary.oilScentsUsed.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Oil Products Used
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Product</TableCell>
                              <TableCell>SKU</TableCell>
                              <TableCell align="right">Total Used</TableCell>
                              <TableCell align="right">Times Used</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {refillHistory.summary.oilScentsUsed.map((oil) => (
                              <TableRow key={oil.productId}>
                                <TableCell>{oil.productName}</TableCell>
                                <TableCell>{oil.sku || 'N/A'}</TableCell>
                                <TableCell align="right">{(oil.totalML / 1000).toFixed(2)} L</TableCell>
                                <TableCell align="right">{oil.count}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Complete Visit History Card */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Complete Visit History ({refillHistory.refillHistory.length} visits)
                  </Typography>

                  {refillHistory.refillHistory.length === 0 ? (
                    <Alert severity="info">No visits recorded for this machine</Alert>
                  ) : (
                    <Box>
                      {refillHistory.refillHistory.map((visit) => (
                        <Paper key={visit.id} elevation={2} sx={{ p: 2, mb: 2, borderLeft: `4px solid ${visit.visitType === 'SERVICE' ? '#EF4444' : visit.visitType === 'SCENT_CHANGE' ? '#8B5CF6' : '#3B82F6'}` }}>
                          {/* Visit Header */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box>
                              <Typography variant="h6" component="span">
                                Visit #{visit.visitNumber}
                              </Typography>
                              <Chip
                                label={visit.visitType.replace('_', ' ')}
                                size="small"
                                sx={{ ml: 2 }}
                                color={visit.visitType === 'SERVICE' ? 'error' : visit.visitType === 'SCENT_CHANGE' ? 'secondary' : 'primary'}
                              />
                              {visit.priority === 'URGENT' && (
                                <Chip label="URGENT" size="small" sx={{ ml: 1 }} color="warning" />
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(visit.visitDate).toLocaleDateString()}
                            </Typography>
                          </Box>

                          <Divider sx={{ my: 1 }} />

                          {/* Visit Details Grid */}
                          <Grid container spacing={2}>
                            {/* Technician */}
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Technician
                              </Typography>
                              <Typography variant="body2">{visit.employeeName}</Typography>
                            </Grid>

                            {/* Status */}
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Status
                              </Typography>
                              <Typography variant="body2">
                                <Chip label={visit.visitStatus.replace('_', ' ')} size="small" />
                              </Typography>
                            </Grid>

                            {/* Service-specific: Issue Resolved */}
                            {visit.visitType === 'SERVICE' && visit.issueResolved !== null && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                  Issue Status
                                </Typography>
                                <Typography variant="body2" color={visit.issueResolved ? 'success.main' : 'error.main'} fontWeight="bold">
                                  {visit.issueResolved ? '✓ Issue Resolved' : '✗ Issue Not Resolved'}
                                </Typography>
                              </Grid>
                            )}

                            {/* Scent Change specific */}
                            {visit.visitType === 'SCENT_CHANGE' && (visit.fromScent || visit.toScent) && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                  Scent Change
                                </Typography>
                                <Typography variant="body2">
                                  {visit.fromScent || 'N/A'} → {visit.toScent || 'N/A'}
                                </Typography>
                              </Grid>
                            )}

                            {/* Request Notes */}
                            {visit.requestNotes && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                  Request Notes
                                </Typography>
                                <Typography variant="body2" sx={{ bgcolor: '#F3F4F6', p: 1, borderRadius: 1, mt: 0.5 }}>
                                  {visit.requestNotes}
                                </Typography>
                              </Grid>
                            )}

                            {/* Refill Details */}
                            {visit.details && visit.details.length > 0 && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                  Refill Details
                                </Typography>
                                {visit.details.map((detail, idx) => (
                                  <Box key={idx} sx={{ bgcolor: '#F9FAFB', p: 1, borderRadius: 1, mt: 0.5, mb: 0.5 }}>
                                    <Typography variant="body2">
                                      <strong>{detail.oilProductName}</strong> ({detail.oilScentName})
                                      {' - '}{detail.amountLiters}L
                                      {' - '}{detail.totalCost.toFixed(2)} SAR
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Warehouse: {detail.warehouseName}
                                    </Typography>
                                    {detail.notes && (
                                      <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                                        Note: {detail.notes}
                                      </Typography>
                                    )}
                                  </Box>
                                ))}
                              </Grid>
                            )}

                            {/* Completion Notes */}
                            {visit.completionNotes && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="success.main" fontWeight="bold">
                                  Completion Notes
                                </Typography>
                                <Typography variant="body2" sx={{ bgcolor: '#F0FDF4', p: 1, borderRadius: 1, mt: 0.5, color: 'success.dark' }}>
                                  {visit.completionNotes}
                                </Typography>
                              </Grid>
                            )}

                            {/* General Visit Notes */}
                            {visit.notes && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                  Visit Notes
                                </Typography>
                                <Typography variant="body2" sx={{ bgcolor: '#FEF3C7', p: 1, borderRadius: 1, mt: 0.5 }}>
                                  {visit.notes}
                                </Typography>
                              </Grid>
                            )}

                            {/* Follow-up Required */}
                            {visit.followupRequired && (
                              <Grid item xs={12}>
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                  ⚠ Follow-up Required
                                </Alert>
                              </Grid>
                            )}
                          </Grid>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerMachineReport;
